import React from "react";

type ButtonVariant = "primary" | "secondary" | "outline" | "danger" | "transparent" | "outline-danger";
type ButtonSize = "small" | "medium" | "large" | "none";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  opacityLoader?: boolean;
  isLoading?: boolean;
  isFullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  baseClassName?: string;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = "primary",
  size = "medium",
  isLoading = false,
  isFullWidth = false,
  leftIcon,
  rightIcon,
  className = "",
  disabled,
  opacityLoader = false,
  baseClassName = "",
  ...props
}) => {
  const baseClasses =
    baseClassName ||
    "rounded focus:outline-none transition-colors duration-200 flex items-center justify-center relative md:min-w-[72px] md:min-h-8 leading-normal";

  const variantClasses = {
    primary: "bg-text-primary text-white transition-all active:bg-brand-dark hover:bg-brand-secondary active:ring active:ring-brand-shade active:ring-offset-1",
    secondary: "bg-white border-borderColor1 border hover:bg-gray-100 text-text-label focus:ring-gray-500",
    outline: "bg-transparent border border-borderColor1 text-gray-600 focus:ring-gray-500",
    "primary-outline": "bg-transparent border border-borderColor1 text-gray-600 focus:ring-gray-500",
    "outline-danger": "bg-transparent border border-text-error leading-20  text-text-error",
    danger: "bg-text-error hover:bg-red-700 text-white focus:ring-red-500",
    transparent: "bg-transparent text-text-default",
  };

  const sizeClasses = {
    small: " px-3 py-1 md:px-4 md:py-1.5 text-13 font-normal",
    medium: "px-4 py-2.5 text-base font-normal",
    large: "px-6 py-3 text-lg",
    none: " !min-w-0",
  };

  const disabledClass = disabled || isLoading ? "opacity-50 cursor-not-allowed" : "";
  const fullWidthClass = isFullWidth ? "w-full" : "";

  const buttonClasses = `outline-none 
    ${baseClasses}
    ${variantClasses[variant]}
    ${sizeClasses[size]}
    ${disabledClass}
    ${fullWidthClass}
    ${className}
  `.trim();

  return (
    <button className={buttonClasses} disabled={disabled || isLoading} {...props}>
      {isLoading && !opacityLoader ? (
        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      ) : (
        <>
          {opacityLoader && isLoading && (
            <div className=" bg-gray-50 w-full absolute top-0 h-full rounded flex items-center justify-center opacity-50">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-100"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
          )}
          {leftIcon && <span className="mr-2">{leftIcon}</span>}
          {children}
          {rightIcon && <span className="ml-2">{rightIcon}</span>}
        </>
      )}
    </button>
  );
};

export default Button;
